import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const STATUS = [["open", "Open"], ["removed", "Removed"], ["dismissed", "Dismissed"], ["all", "All"]];
const TONE = { open: "bg-[#F59E0B]/15 text-[#FBBF24]", removed: "bg-[#EF4444]/15 text-[#F87171]", dismissed: "bg-[#2D3442] text-[#A8A090]" };

const Report = ({ r, onDone }) => {
  const [note, setNote] = useState("");
  const act = async (path, msg) => { try { await api.post(path, path.includes("restore") ? {} : { note }); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <li className="space-y-3 p-4" data-testid={`rights-row-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <a href={`/read/${r.story_id}`} target="_blank" rel="noreferrer" className="font-display text-lg text-[#F3EBDD] hover:text-[#F59E0B]">{r.story_title}</a>
          {r.fan_work && <span className="ml-2 text-xs text-[#C4B5FD]">Fan work · {r.fan_source}</span>}
          <div className="text-xs text-[#A8A090]">Sharing: {r.story_sharing}{r.story_hold ? " · held" : ""} · {new Date(r.created_at).toLocaleString()}</div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${TONE[r.status]}`} data-testid={`rights-status-${r.id}`}>{r.status}</span>
      </div>
      <div className="text-sm text-[#D6CCBC]"><b className="text-[#F3EBDD]">{r.name}</b>{r.organization && ` (${r.organization})`} · <a href={`mailto:${r.email}`} className="text-[#F59E0B]">{r.email}</a></div>
      <div className="text-sm text-[#D6CCBC]"><span className="text-[#A8A090]">Their work:</span> {r.work}</div>
      <p className="whitespace-pre-wrap rounded border border-[#2D3442] bg-[#13171F] p-3 text-sm text-[#D6CCBC]">{r.details}</p>
      {r.admin_note && <p className="text-xs text-[#A8A090]">Admin note: {r.admin_note}</p>}
      {r.status === "open" && <>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note (included in the email to the writer if you remove the story)" data-testid={`rights-note-${r.id}`} />
        <div className="flex gap-2">
          <Button size="sm" variant="destructive" onClick={() => act(`/admin/rights-reports/${r.id}/remove`, "Story removed from Reader Mode")} data-testid={`rights-remove-${r.id}`}>Remove from Reader Mode</Button>
          <Button size="sm" variant="outline" onClick={() => act(`/admin/rights-reports/${r.id}/dismiss`, "Report dismissed")} data-testid={`rights-dismiss-${r.id}`}>Dismiss</Button>
        </div>
      </>}
      {r.story_hold && r.status === "removed" && <Button size="sm" variant="ghost" onClick={() => act(`/admin/stories/${r.story_id}/rights-restore`, "Writer can share the story again")} data-testid={`rights-restore-${r.id}`}>Allow the writer to share again</Button>}
    </li>
  );
};

export const RightsReportsPanel = () => {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const { data = [] } = useQuery({ queryKey: ["admin-rights", status], queryFn: () => api.get("/admin/rights-reports", { params: status === "all" ? {} : { status } }).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-rights"] });
  return (
    <section className="space-y-5 rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-rights-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Flag className="h-4 w-4 text-[#FBBF24]" /><div className="lf-label">Rights reports</div></div>
          <p className="mt-2 text-xs text-[#A8A090]">Removing a story turns off its Reader sharing and stops the writer from sharing it again. Their private copy is untouched, and they get an email.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="rights-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data.length ? <ul className="divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="rights-list">{data.map((r) => <Report key={r.id} r={r} onDone={refresh} />)}</ul>
        : <p className="text-sm text-[#A8A090]" data-testid="rights-empty">No reports here.</p>}
    </section>
  );
};
